import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  CreditCard, Crown, Clock, CheckCircle, AlertCircle, 
  Mail, Bell, Settings as SettingsIcon, Zap, Shield, User,
  Building2, Save, DollarSign, ArrowLeft, LogOut, Plus, Edit, Trash2, Star, Wallet, Loader2,
  FileText, Palette, Layout, Eye, Globe
} from 'lucide-react';
import { subscription, auth, paymentMethods as paymentMethodsAPI, businesses, invoiceSettings, admin as adminAPI, notificationPrefs as notifPrefsAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import { sessionManager } from '../utils/auth';
import { useTranslation } from 'react-i18next';
import { CURRENCY_OPTIONS } from '../utils/currency';

const Settings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('subscription');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [languageCurrencyLoading, setLanguageCurrencyLoading] = useState(false);
  
  // PayPal configuration
  const [paypalClientId, setPaypalClientId] = useState(null);
  
  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [businessList, setBusinessList] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [isPaymentMethodDialogOpen, setIsPaymentMethodDialogOpen] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [paymentMethodForm, setPaymentMethodForm] = useState({
    name: '',
    type: 'bank',
    details: '',
    is_default: false
  });
  
  // Invoice Settings
  const [invoiceSettingsData, setInvoiceSettingsData] = useState({
    template: 'classic',
    color_scheme: 'blue',
    custom_color: '#2563EB',
    show_logo: true,
    show_business_name: true,
    show_payment_info: true,
    footer_text: ''
  });
  
  // Contact Admin
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubject, setContactSubject] = useState('general');
  const [sendingContact, setSendingContact] = useState(false);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: ''
  });
  
  // Admin payment settings (only visible for admin)
  const [paymentSettings, setPaymentSettings] = useState({
    paypalEmail: '',
    paypalClientId: '',
    stripePublicKey: '',
    bankName: '',
    bankAccount: '',
    bankRouting: ''
  });
  
  // Admin user stats
  const [userStats, setUserStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [userActivity, setUserActivity] = useState(null);
  const [activitySearch, setActivitySearch] = useState('');
  const [subscribersDialogOpen, setSubscribersDialogOpen] = useState(false);
  const [subscribers, setSubscribers] = useState(null);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [activityDetailUser, setActivityDetailUser] = useState(null);
  const [activityDetail, setActivityDetail] = useState(null);
  
  // Email notification preferences
  const [emailPrefs, setEmailPrefs] = useState({
    sendOverdueReminders: true,
    reminderFrequency: 'weekly',
    daysBeforeDue: 3,
    includePaymentLink: true
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchSubscriptionStatus();
    loadEmailPreferences();
    loadPaymentSettings();
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchPaymentMethods();
      loadEmailPreferences();
    }
  }, [selectedBusiness]);

  const fetchCurrentUser = async () => {
    try {
      const response = await auth.me();
      setCurrentUser(response.data);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        company: response.data.company || '',
        address: response.data.address || ''
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const handleSaveLanguageCurrency = async (businessId, updates) => {
    if (!businessId) return;
    setLanguageCurrencyLoading(true);
    try {
      await businesses.update(businessId, updates);
      // Update local state
      setBusinessList((prev) => prev.map(b => b.id === businessId ? { ...b, ...updates } : b));
      // If language changed, switch i18n
      if (updates.language) {
        i18n.changeLanguage(updates.language);
      }
      toast({ title: t('common.save'), description: 'Saved successfully' });
    } catch (error) {
      console.error('Error saving language/currency:', error);
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    } finally {
      setLanguageCurrencyLoading(false);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const response = await businesses.getAll();
      setBusinessList(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedBusiness(response.data[0].id);
        try {
          const settingsRes = await invoiceSettings.get(response.data[0].id);
          setInvoiceSettingsData(settingsRes.data);
        } catch (e) {
          console.error('Error fetching invoice settings:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await paymentMethodsAPI.getAll(selectedBusiness);
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handleSavePaymentMethod = async (e) => {
    e.preventDefault();
    try {
      if (editingPaymentMethod) {
        await paymentMethodsAPI.update(editingPaymentMethod.id, paymentMethodForm);
        toast({ title: 'Success', description: 'Payment method updated' });
      } else {
        await paymentMethodsAPI.create({
          business_id: selectedBusiness,
          ...paymentMethodForm
        });
        toast({ title: 'Success', description: 'Payment method added' });
      }
      setIsPaymentMethodDialogOpen(false);
      setEditingPaymentMethod(null);
      setPaymentMethodForm({ name: '', type: 'bank', details: '', is_default: false });
      fetchPaymentMethods();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save payment method', variant: 'destructive' });
    }
  };

  const handleEditPaymentMethod = (method) => {
    setEditingPaymentMethod(method);
    setPaymentMethodForm({
      name: method.name,
      type: method.type,
      details: method.details,
      is_default: method.is_default
    });
    setIsPaymentMethodDialogOpen(true);
  };

  const handleDeletePaymentMethod = async (id) => {
    if (!window.confirm('Delete this payment method?')) return;
    try {
      await paymentMethodsAPI.delete(id);
      toast({ title: 'Success', description: 'Payment method deleted' });
      fetchPaymentMethods();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleSetDefaultPaymentMethod = async (id) => {
    try {
      await paymentMethodsAPI.update(id, { is_default: true });
      toast({ title: 'Success', description: 'Default payment method updated' });
      fetchPaymentMethods();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to set default', variant: 'destructive' });
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await subscription.getStatus();
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription status',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEmailPreferences = async () => {
    if (!selectedBusiness) {
      const saved = localStorage.getItem('turbobooks_email_prefs');
      if (saved) setEmailPrefs(JSON.parse(saved));
      return;
    }
    try {
      const response = await notifPrefsAPI.get(selectedBusiness);
      setEmailPrefs(response.data);
    } catch {
      const saved = localStorage.getItem('turbobooks_email_prefs');
      if (saved) setEmailPrefs(JSON.parse(saved));
    }
  };

  const loadPaymentSettings = () => {
    const saved = localStorage.getItem('turbobooks_payment_settings');
    if (saved) {
      setPaymentSettings(JSON.parse(saved));
    }
  };

  const saveEmailPreferences = async (newPrefs) => {
    setEmailPrefs(newPrefs);
    if (selectedBusiness) {
      try {
        await notifPrefsAPI.update(selectedBusiness, newPrefs);
      } catch {
        localStorage.setItem('turbobooks_email_prefs', JSON.stringify(newPrefs));
      }
    } else {
      localStorage.setItem('turbobooks_email_prefs', JSON.stringify(newPrefs));
    }
    toast({
      title: 'Preferences Saved',
      description: 'Your email notification preferences have been updated.',
    });
  };

  const savePaymentSettings = () => {
    localStorage.setItem('turbobooks_payment_settings', JSON.stringify(paymentSettings));
    toast({
      title: 'Payment Settings Saved',
      description: 'Your payment receiving accounts have been updated.',
    });
  };

  const fetchUserStats = async () => {
    setStatsLoading(true);
    try {
      const [userRes, visitorRes, activityRes] = await Promise.all([
        adminAPI.getUserStats(),
        adminAPI.getVisitorStats(),
        adminAPI.getUserActivity({ limit: 50 }),
      ]);
      setUserStats(userRes.data);
      setVisitorStats(visitorRes.data);
      setUserActivity(activityRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const refreshUserActivity = async (search = '') => {
    try {
      const res = await adminAPI.getUserActivity({ limit: 50, search: search || undefined });
      setUserActivity(res.data);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const openSubscribers = async () => {
    setSubscribersDialogOpen(true);
    if (!subscribers) {
      setSubscribersLoading(true);
      try {
        const res = await adminAPI.getActiveSubscribers();
        setSubscribers(res.data);
      } catch (error) {
        console.error('Failed to load subscribers:', error);
        toast({ title: 'Error', description: 'Could not load subscribers', variant: 'destructive' });
      } finally {
        setSubscribersLoading(false);
      }
    }
  };

  const openUserActivityDetail = async (u) => {
    setActivityDetailUser(u);
    setActivityDetail(null);
    try {
      const res = await adminAPI.getUserActivityDetail(u.user_id);
      setActivityDetail(res.data);
    } catch (error) {
      console.error('Failed to load activity detail:', error);
    }
  };

  const [subEditUser, setSubEditUser] = useState(null);
  const [subEditMonths, setSubEditMonths] = useState('indefinite');
  const [subEditLoading, setSubEditLoading] = useState(false);

  const submitSubscriptionChange = async (action) => {
    if (!subEditUser) return;
    setSubEditLoading(true);
    try {
      const months = action === 'activate' && subEditMonths !== 'indefinite' ? parseInt(subEditMonths, 10) : null;
      const res = await adminAPI.updateUserSubscription(subEditUser.user_id, { action, months });
      toast({ title: 'Subscription updated', description: res.data.message });
      setSubEditUser(null);
      await refreshUserActivity(activitySearch);
      setSubscribers(null);
      fetchUserStats();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update subscription', variant: 'destructive' });
    } finally {
      setSubEditLoading(false);
    }
  };

  const formatDocNumber = (prefix, next) => {
    const seq = String(next || 1).padStart(3, '0');
    const year = invoiceSettingsData.number_include_year !== false ? `${new Date().getFullYear()}-` : '';
    return `${prefix}-${year}${seq}`;
  };

  const formatSubEnd = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (d.getFullYear() >= 2099) return 'No expiry';
    return d.toLocaleDateString();
  };

  const formatRelative = (iso) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  // Load admin stats when admin tab is active
  useEffect(() => {
    if (activeTab === 'admin' && subscriptionStatus?.is_admin && !userStats) {
      fetchUserStats();
    }
  }, [activeTab, subscriptionStatus]);

  const handleUpdateProfile = async () => {
    setProfileLoading(true);
    try {
      // Store in localStorage for now (would be API call in production)
      localStorage.setItem('turbobooks_profile', JSON.stringify(profileData));
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleStripeCheckout = async () => {
    try {
      setCheckoutLoading(true);
      const response = await subscription.createCheckout();
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePayPalCheckout = async () => {
    setPaypalLoading(true);
    try {
      const response = await subscription.createPayPalOrder();
      const { approval_url, order_id } = response.data;
      
      if (approval_url) {
        // Store order_id for capture after redirect
        localStorage.setItem('paypal_order_id', order_id);
        // Redirect to PayPal
        window.location.href = approval_url;
      } else {
        throw new Error('No approval URL returned');
      }
    } catch (error) {
      console.error('PayPal checkout error:', error);
      toast({
        title: 'PayPal Error',
        description: error.response?.data?.detail || 'Failed to start PayPal checkout. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setPaypalLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;
    
    const { status, is_admin } = subscriptionStatus;
    
    if (is_admin) {
      return <Badge className="bg-purple-600 text-white gap-1"><Crown className="h-3 w-3" /> Admin</Badge>;
    }
    
    switch (status) {
      case 'active':
        return <Badge className="bg-green-600 text-white gap-1"><CheckCircle className="h-3 w-3" /> Premium</Badge>;
      case 'trial':
        return <Badge className="bg-blue-600 text-white gap-1"><Clock className="h-3 w-3" /> Trial</Badge>;
      case 'expired':
        return <Badge className="bg-red-600 text-white gap-1"><AlertCircle className="h-3 w-3" /> Free</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const isAdmin = subscriptionStatus?.is_admin;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await auth.logout();
      sessionManager.clearToken();
      navigate('/login');
    } catch (error) {
      sessionManager.clearToken();
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <SettingsIcon className="h-6 w-6 text-blue-600" />
                <h1 className="text-xl font-bold text-gray-800">Settings</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {currentUser && (
                <span className="text-sm text-gray-600">{currentUser.email}</span>
              )}
              <Button variant="outline" onClick={handleLogout} className="gap-2 text-red-600 hover:bg-red-50">
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap w-full gap-1 h-auto mb-6">
            <TabsTrigger value="subscription" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="payment-methods" className="gap-2">
              <Wallet className="h-4 w-4" />
              Payment Methods
            </TabsTrigger>
            <TabsTrigger value="invoice-settings" className="gap-2">
              <FileText className="h-4 w-4" />
              Invoice Style
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="language-currency" className="gap-2" data-testid="settings-tab-language-currency">
              <Globe className="h-4 w-4" />
              Language & Currency
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2">
              <Mail className="h-4 w-4" />
              Contact
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="admin" className="gap-2">
              <Crown className="h-4 w-4" />
              Admin
            </TabsTrigger>
          )}
        </TabsList>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <Card className="shadow-lg border-t-4 border-t-blue-600">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  <div>
                    <CardTitle>Subscription</CardTitle>
                    <CardDescription>Your current plan and billing</CardDescription>
                  </div>
                </div>
                {getStatusBadge()}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {subscriptionStatus && (
                <>
                  {/* Status Message */}
                  <div className={`p-4 rounded-lg ${
                    subscriptionStatus.is_admin ? 'bg-purple-50 border border-purple-200' :
                    subscriptionStatus.status === 'active' ? 'bg-green-50 border border-green-200' :
                    subscriptionStatus.status === 'trial' ? 'bg-blue-50 border border-blue-200' :
                    'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`font-medium ${
                      subscriptionStatus.is_admin ? 'text-purple-800' :
                      subscriptionStatus.status === 'active' ? 'text-green-800' :
                      subscriptionStatus.status === 'trial' ? 'text-blue-800' :
                      'text-red-800'
                    }`}>
                      {subscriptionStatus.message}
                    </p>
                    {subscriptionStatus.days_left !== undefined && !subscriptionStatus.is_admin && (
                      <p className="text-sm text-gray-600 mt-1">
                        {subscriptionStatus.days_left} days remaining
                      </p>
                    )}
                  </div>

                  {/* Feature Limits */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Businesses</p>
                      <p className="font-semibold">
                        {subscriptionStatus.limits?.max_businesses >= 999 ? 'Unlimited' : subscriptionStatus.limits?.max_businesses}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Invoices/Month</p>
                      <p className="font-semibold">
                        {subscriptionStatus.limits?.max_invoices_per_month >= 999999 ? 'Unlimited' : subscriptionStatus.limits?.max_invoices_per_month}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Exports</p>
                      <p className="font-semibold">
                        {subscriptionStatus.limits?.can_export ? '✓ Enabled' : '✗ Disabled'}
                      </p>
                    </div>
                  </div>

                  {/* Payment Options (only for non-subscribed users) */}
                  {!subscriptionStatus.is_subscribed && !subscriptionStatus.is_admin && (
                    <div className="pt-4 border-t">
                      <div className="mb-6">
                        <p className="font-semibold text-lg">Upgrade to Premium</p>
                        <p className="text-sm text-gray-600">
                          ${subscriptionStatus.price}/month - Unlock all features
                        </p>
                      </div>

                      {/* Payment Methods */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Stripe */}
                        <Card className="border-2 hover:border-blue-400 transition-colors cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-blue-100 p-2 rounded-lg">
                                <CreditCard className="h-6 w-6 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-semibold">Credit/Debit Card</p>
                                <p className="text-xs text-gray-500">Powered by Stripe</p>
                              </div>
                            </div>
                            <Button 
                              onClick={handleStripeCheckout}
                              disabled={checkoutLoading}
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              data-testid="stripe-checkout-btn"
                            >
                              {checkoutLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              ) : (
                                'Pay with Card'
                              )}
                            </Button>
                          </CardContent>
                        </Card>

                        {/* PayPal */}
                        <Card className="border-2 hover:border-yellow-400 transition-colors cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-yellow-100 p-2 rounded-lg">
                                <DollarSign className="h-6 w-6 text-yellow-600" />
                              </div>
                              <div>
                                <p className="font-semibold">PayPal</p>
                                <p className="text-xs text-gray-500">Pay with PayPal balance or card</p>
                              </div>
                            </div>
                            <Button 
                              onClick={handlePayPalCheckout}
                              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
                              data-testid="paypal-checkout-btn"
                              disabled={paypalLoading}
                            >
                              {paypalLoading ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                'Pay with PayPal'
                              )}
                            </Button>
                          </CardContent>
                        </Card>

                        {/* Lasco */}
                        <Card className="border-2 hover:border-gray-600 transition-colors cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="bg-gray-800 p-2 rounded-lg">
                                <Wallet className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <p className="font-semibold">Lasco Pay</p>
                                <p className="text-xs text-gray-500">Powered by Lasco</p>
                              </div>
                            </div>
                            <a
                              href="https://pay.lascobizja.com/btn/qy209UXK8D6e"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                              data-testid="lasco-checkout-btn"
                            >
                              <Button 
                                className="w-full text-white"
                                style={{ background: '#383635' }}
                              >
                                Pay with Lasco
                              </Button>
                            </a>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Premium Features List */}
                      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-3">Premium includes:</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Unlimited businesses
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Unlimited invoices
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            PDF & Excel exports
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Email reminders
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Advanced reports
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Priority support
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <User className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>Update your personal and business details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="profile_name">Full Name</Label>
                  <Input
                    id="profile_name"
                    value={profileData.name}
                    onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="profile_email">Email</Label>
                  <Input
                    id="profile_email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                    placeholder="your@email.com"
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="profile_phone">Phone Number</Label>
                  <Input
                    id="profile_phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <Label htmlFor="profile_company">Company Name</Label>
                  <Input
                    id="profile_company"
                    value={profileData.company}
                    onChange={(e) => setProfileData({...profileData, company: e.target.value})}
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="profile_address">Business Address</Label>
                <Textarea
                  id="profile_address"
                  value={profileData.address}
                  onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                  placeholder="Enter your business address"
                  rows={3}
                />
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleUpdateProfile}
                  disabled={profileLoading}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                >
                  {profileLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Profile
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Methods Tab */}
        <TabsContent value="payment-methods">
          <Card className="shadow-lg border-t-4 border-t-green-600">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wallet className="h-6 w-6 text-green-600" />
                  <div>
                    <CardTitle>Payment Methods</CardTitle>
                    <CardDescription>Manage payment methods that appear on your invoices</CardDescription>
                  </div>
                </div>
                <Dialog open={isPaymentMethodDialogOpen} onOpenChange={(open) => {
                  setIsPaymentMethodDialogOpen(open);
                  if (!open) {
                    setEditingPaymentMethod(null);
                    setPaymentMethodForm({ name: '', type: 'bank', details: '', is_default: false });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button className="gap-2 bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4" />
                      Add Payment Method
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingPaymentMethod ? 'Edit' : 'Add'} Payment Method</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSavePaymentMethod} className="space-y-4">
                      <div>
                        <Label htmlFor="pm_name">Name *</Label>
                        <Input
                          id="pm_name"
                          value={paymentMethodForm.name}
                          onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, name: e.target.value })}
                          placeholder="e.g., Business Bank Account, PayPal"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="pm_type">Type</Label>
                        <Select value={paymentMethodForm.type} onValueChange={(v) => setPaymentMethodForm({ ...paymentMethodForm, type: v })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bank">Bank Transfer</SelectItem>
                            <SelectItem value="paypal">PayPal</SelectItem>
                            <SelectItem value="stripe">Credit Card (Stripe)</SelectItem>
                            <SelectItem value="cash">Cash</SelectItem>
                            <SelectItem value="check">Check</SelectItem>
                            <SelectItem value="zelle">Zelle</SelectItem>
                            <SelectItem value="venmo">Venmo</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="pm_details">Payment Details *</Label>
                        <Textarea
                          id="pm_details"
                          value={paymentMethodForm.details}
                          onChange={(e) => setPaymentMethodForm({ ...paymentMethodForm, details: e.target.value })}
                          placeholder="Enter bank account details, PayPal email, or other payment instructions..."
                          rows={4}
                          required
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={paymentMethodForm.is_default}
                          onCheckedChange={(checked) => setPaymentMethodForm({ ...paymentMethodForm, is_default: checked })}
                        />
                        <Label>Set as default payment method</Label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setIsPaymentMethodDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-green-600 hover:bg-green-700">
                          {editingPaymentMethod ? 'Update' : 'Add'} Payment Method
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Business Selector */}
              {businessList.length > 1 && (
                <div className="mb-4">
                  <Label>Select Business</Label>
                  <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {businessList.map((biz) => (
                        <SelectItem key={biz.id} value={biz.id}>{biz.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {paymentMethods.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No payment methods yet</p>
                  <p className="text-gray-400 text-sm">Add payment methods that will appear on your invoices</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div key={method.id} className="border rounded-lg p-4 flex items-start justify-between hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${method.is_default ? 'bg-green-100' : 'bg-gray-100'}`}>
                          <CreditCard className={`h-5 w-5 ${method.is_default ? 'text-green-600' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{method.name}</h4>
                            {method.is_default && (
                              <Badge className="bg-green-100 text-green-800">Default</Badge>
                            )}
                          </div>
                          <Badge variant="outline" className="mt-1">{method.type}</Badge>
                          <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{method.details}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!method.is_default && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSetDefaultPaymentMethod(method.id)}
                            title="Set as default"
                          >
                            <Star className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditPaymentMethod(method)}
                          className="text-blue-600"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeletePaymentMethod(method.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Settings Tab */}
        <TabsContent value="invoice-settings">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Layout className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle>Invoice & Estimate Style</CardTitle>
                  <CardDescription>Customize the appearance of your invoices and estimates</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Business Selector */}
              <div>
                <Label className="text-sm font-medium">Select Business</Label>
                <Select value={selectedBusiness} onValueChange={async (val) => {
                  setSelectedBusiness(val);
                  // Fetch invoice settings for this business
                  try {
                    const response = await invoiceSettings.get(val);
                    setInvoiceSettingsData(response.data);
                  } catch (error) {
                    console.error('Failed to fetch invoice settings');
                  }
                }}>
                  <SelectTrigger className="mt-2" data-testid="invoice-settings-business-select">
                    <SelectValue placeholder="Select a business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessList.map(biz => (
                      <SelectItem key={biz.id} value={biz.id}>{biz.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedBusiness && (
                <>
                  {/* Template Selection */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Invoice Template
                    </Label>
                    <p className="text-sm text-gray-500 mb-3">Choose a layout style for your invoices</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {[
                        { id: 'classic', name: 'Classic', desc: 'Traditional professional' },
                        { id: 'modern', name: 'Modern', desc: 'Clean minimalist' },
                        { id: 'compact', name: 'Compact', desc: 'Space efficient' },
                        { id: 'detailed', name: 'Detailed', desc: 'Extra info sections' },
                        { id: 'bold', name: 'Bold Header', desc: 'Large logo focus' },
                        { id: 'striped', name: 'Striped', desc: 'Alternating rows' },
                        { id: 'boxed', name: 'Boxed', desc: 'Bordered items' },
                        { id: 'simple', name: 'Simple', desc: 'Just essentials' },
                        { id: 'executive', name: 'Executive', desc: 'Premium feel' },
                        { id: 'creative', name: 'Creative', desc: 'Unique layout' }
                      ].map(template => (
                        <div
                          key={template.id}
                          onClick={() => setInvoiceSettingsData({...invoiceSettingsData, template: template.id})}
                          className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                            invoiceSettingsData.template === template.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`template-${template.id}`}
                        >
                          <p className="font-medium text-sm">{template.name}</p>
                          <p className="text-xs text-gray-500">{template.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Color Scheme */}
                  <div>
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Palette className="h-4 w-4" />
                      Color Scheme
                    </Label>
                    <p className="text-sm text-gray-500 mb-3">Select a color theme for your invoices</p>
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                      {[
                        { id: 'blue', color: '#2563EB', name: 'Blue' },
                        { id: 'green', color: '#059669', name: 'Green' },
                        { id: 'red', color: '#DC2626', name: 'Red' },
                        { id: 'purple', color: '#7C3AED', name: 'Purple' },
                        { id: 'orange', color: '#EA580C', name: 'Orange' },
                        { id: 'teal', color: '#0D9488', name: 'Teal' },
                        { id: 'gray', color: '#4B5563', name: 'Gray' },
                        { id: 'black', color: '#171717', name: 'Black' },
                        { id: 'navy', color: '#1E3A5F', name: 'Navy' },
                        { id: 'custom', color: invoiceSettingsData.custom_color || '#2563EB', name: 'Custom' }
                      ].map(scheme => (
                        <div
                          key={scheme.id}
                          onClick={() => setInvoiceSettingsData({...invoiceSettingsData, color_scheme: scheme.id})}
                          className={`flex flex-col items-center p-2 border-2 rounded-lg cursor-pointer transition-all ${
                            invoiceSettingsData.color_scheme === scheme.id 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`color-${scheme.id}`}
                        >
                          <div 
                            className="w-8 h-8 rounded-full mb-1 border-2 border-white shadow"
                            style={{ backgroundColor: scheme.color }}
                          />
                          <p className="text-xs">{scheme.name}</p>
                        </div>
                      ))}
                    </div>
                    
                    {/* Custom Color Picker */}
                    {invoiceSettingsData.color_scheme === 'custom' && (
                      <div className="mt-3 flex items-center gap-3">
                        <Label>Custom Color:</Label>
                        <Input
                          type="color"
                          value={invoiceSettingsData.custom_color || '#2563EB'}
                          onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, custom_color: e.target.value})}
                          className="w-16 h-10 p-1 cursor-pointer"
                          data-testid="custom-color-picker"
                        />
                        <Input
                          value={invoiceSettingsData.custom_color || '#2563EB'}
                          onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, custom_color: e.target.value})}
                          className="w-28 font-mono"
                          placeholder="#2563EB"
                        />
                      </div>
                    )}
                  </div>

                  {/* Additional Options */}
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">Additional Options</Label>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Show Business Logo</p>
                        <p className="text-sm text-gray-500">Display your logo on invoices</p>
                      </div>
                      <Switch
                        checked={invoiceSettingsData.show_logo}
                        onCheckedChange={(val) => setInvoiceSettingsData({...invoiceSettingsData, show_logo: val})}
                        data-testid="show-logo-switch"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Show Business Name</p>
                        <p className="text-sm text-gray-500">Display business name header on invoices</p>
                      </div>
                      <Switch
                        checked={invoiceSettingsData.show_business_name}
                        onCheckedChange={(val) => setInvoiceSettingsData({...invoiceSettingsData, show_business_name: val})}
                        data-testid="show-business-name-switch"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Show Payment Information</p>
                        <p className="text-sm text-gray-500">Display payment details on invoices</p>
                      </div>
                      <Switch
                        checked={invoiceSettingsData.show_payment_info}
                        onCheckedChange={(val) => setInvoiceSettingsData({...invoiceSettingsData, show_payment_info: val})}
                        data-testid="show-payment-switch"
                      />
                    </div>
                    
                    <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4 space-y-4" data-testid="numbering-settings">
                      <div>
                        <p className="font-medium">Invoice & Estimate Numbering</p>
                        <p className="text-sm text-gray-500">Choose the prefix and the next number for new documents</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Invoice Prefix</Label>
                          <Input
                            value={invoiceSettingsData.invoice_prefix || ''}
                            onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, invoice_prefix: e.target.value.toUpperCase()})}
                            placeholder="INV"
                            maxLength={12}
                            className="mt-2 font-mono"
                            data-testid="invoice-prefix-input"
                          />
                        </div>
                        <div>
                          <Label>Next Invoice Number</Label>
                          <Input
                            type="number"
                            min={1}
                            value={invoiceSettingsData.invoice_next_number ?? ''}
                            onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, invoice_next_number: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                            className="mt-2 font-mono"
                            data-testid="invoice-next-number-input"
                          />
                        </div>
                        <div>
                          <Label>Estimate Prefix</Label>
                          <Input
                            value={invoiceSettingsData.estimate_prefix || ''}
                            onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, estimate_prefix: e.target.value.toUpperCase()})}
                            placeholder="EST"
                            maxLength={12}
                            className="mt-2 font-mono"
                            data-testid="estimate-prefix-input"
                          />
                        </div>
                        <div>
                          <Label>Next Estimate Number</Label>
                          <Input
                            type="number"
                            min={1}
                            value={invoiceSettingsData.estimate_next_number ?? ''}
                            onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, estimate_next_number: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                            className="mt-2 font-mono"
                            data-testid="estimate-next-number-input"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Include Year</p>
                          <p className="text-xs text-gray-500">Adds the current year between the prefix and number</p>
                        </div>
                        <Switch
                          checked={invoiceSettingsData.number_include_year !== false}
                          onCheckedChange={(val) => setInvoiceSettingsData({...invoiceSettingsData, number_include_year: val})}
                          data-testid="number-include-year-switch"
                        />
                      </div>
                      <div className="text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
                        <span>Next invoice: <span className="font-mono font-semibold text-blue-700" data-testid="invoice-number-preview">{formatDocNumber(invoiceSettingsData.invoice_prefix || 'INV', invoiceSettingsData.invoice_next_number)}</span></span>
                        <span>Next estimate: <span className="font-mono font-semibold text-blue-700" data-testid="estimate-number-preview">{formatDocNumber(invoiceSettingsData.estimate_prefix || 'EST', invoiceSettingsData.estimate_next_number)}</span></span>
                      </div>
                    </div>

                    <div>
                      <Label>Invoice Footer Text</Label>
                      <Textarea
                        value={invoiceSettingsData.footer_text || ''}
                        onChange={(e) => setInvoiceSettingsData({...invoiceSettingsData, footer_text: e.target.value})}
                        placeholder="e.g., Thank you for your business! Payment due within 30 days."
                        rows={2}
                        className="mt-2"
                        data-testid="footer-text-input"
                      />
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={async () => {
                      try {
                        const payload = { ...invoiceSettingsData };
                        ['invoice_next_number', 'estimate_next_number'].forEach((k) => { if (payload[k] === '' || Number.isNaN(payload[k])) delete payload[k]; });
                        const res = await invoiceSettings.update(selectedBusiness, payload);
                        setInvoiceSettingsData(res.data);
                        toast({
                          title: 'Settings Saved',
                          description: 'Your invoice style settings have been updated.'
                        });
                      } catch (error) {
                        toast({
                          title: 'Error',
                          description: 'Failed to save settings',
                          variant: 'destructive'
                        });
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                    data-testid="save-invoice-settings-btn"
                  >
                    <Save className="h-4 w-4" />
                    Save Invoice Settings
                  </Button>
                </>
              )}

              {!selectedBusiness && (
                <div className="text-center py-8 text-gray-500">
                  <Layout className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a business to customize invoice settings</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle>Email Notifications</CardTitle>
                  <CardDescription>Configure how and when you send invoice reminders</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Auto-send Overdue Reminders</Label>
                  <p className="text-sm text-gray-500">Automatically send reminders for overdue invoices</p>
                </div>
                <Switch
                  checked={emailPrefs.sendOverdueReminders}
                  onCheckedChange={(checked) => saveEmailPreferences({...emailPrefs, sendOverdueReminders: checked})}
                  data-testid="auto-reminder-switch"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Reminder Frequency</Label>
                <Select 
                  value={emailPrefs.reminderFrequency}
                  onValueChange={(value) => saveEmailPreferences({...emailPrefs, reminderFrequency: value})}
                >
                  <SelectTrigger className="w-full md:w-64" data-testid="reminder-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="every3days">Every 3 Days</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Send Reminder Before Due Date</Label>
                <p className="text-sm text-gray-500">Send a reminder this many days before the invoice is due</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={emailPrefs.daysBeforeDue}
                    onChange={(e) => saveEmailPreferences({...emailPrefs, daysBeforeDue: parseInt(e.target.value) || 0})}
                    className="w-24"
                    data-testid="days-before-due-input"
                  />
                  <span className="text-sm text-gray-600">days</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base font-medium">Include Payment Information</Label>
                  <p className="text-sm text-gray-500">Include your payment details in reminder emails</p>
                </div>
                <Switch
                  checked={emailPrefs.includePaymentLink}
                  onCheckedChange={(checked) => saveEmailPreferences({...emailPrefs, includePaymentLink: checked})}
                  data-testid="include-payment-switch"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4" data-testid="auto-reminder-info">
                <p className="text-sm text-blue-800 font-medium">How Automatic Reminders Work</p>
                <p className="text-sm text-blue-700 mt-1">
                  When enabled, TurboBooks automatically checks for overdue invoices and sends email reminders to your clients 
                  at the frequency you specify. Reminders include the invoice number, amount owed, and days overdue. 
                  Clients must have an email address saved in their profile to receive reminders.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language & Currency Tab */}
        <TabsContent value="language-currency">
          <Card className="shadow-lg border-t-4 border-t-blue-600">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Globe className="h-6 w-6 text-blue-600" />
                <div>
                  <CardTitle data-testid="lang-currency-title">{t('settings.language_currency')}</CardTitle>
                  <CardDescription>{t('settings.language_help')}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* App Language */}
              <div>
                <Label htmlFor="app-language" className="text-base font-semibold">
                  {t('settings.language_for_app')}
                </Label>
                <p className="text-sm text-gray-500 mb-3">{t('settings.language_help')}</p>
                <Select
                  value={i18n.language || 'en'}
                  onValueChange={(value) => {
                    i18n.changeLanguage(value);
                    localStorage.setItem('turbobooks_lang', value);
                    toast({ title: t('common.save'), description: t('settings.language_for_app') + ' ✓' });
                  }}
                >
                  <SelectTrigger id="app-language" data-testid="app-language-select" className="max-w-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">{t('languages.en')}</SelectItem>
                    <SelectItem value="es">{t('languages.es')}</SelectItem>
                    <SelectItem value="fr">{t('languages.fr')}</SelectItem>
                    <SelectItem value="nl">{t('languages.nl')}</SelectItem>
                    <SelectItem value="pt">{t('languages.pt')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Per-business Currency & PDF Language */}
              <div className="border-t pt-6">
                <h3 className="text-base font-semibold mb-1">{t('settings.currency_for_business')}</h3>
                <p className="text-sm text-gray-500 mb-4">{t('settings.currency_help')}</p>
                {businessList.length === 0 ? (
                  <p className="text-sm text-gray-400">{t('common.no_data')}</p>
                ) : (
                  <div className="space-y-4">
                    {businessList.map((biz) => (
                      <div key={biz.id} className="border rounded-lg p-4 bg-gray-50" data-testid={`biz-currency-row-${biz.id}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Building2 className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{biz.name}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">{t('common.currency')}</Label>
                            <Select
                              value={biz.currency || 'USD'}
                              onValueChange={(value) => handleSaveLanguageCurrency(biz.id, { currency: value })}
                            >
                              <SelectTrigger data-testid={`biz-currency-${biz.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {CURRENCY_OPTIONS.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>
                                    {c.symbol} {c.code} — {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">PDF / Invoice Language</Label>
                            <Select
                              value={biz.language || 'en'}
                              onValueChange={(value) => handleSaveLanguageCurrency(biz.id, { language: value })}
                            >
                              <SelectTrigger data-testid={`biz-language-${biz.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="en">{t('languages.en')}</SelectItem>
                                <SelectItem value="es">{t('languages.es')}</SelectItem>
                                <SelectItem value="fr">{t('languages.fr')}</SelectItem>
                                <SelectItem value="nl">{t('languages.nl')}</SelectItem>
                                <SelectItem value="pt">{t('languages.pt')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {languageCurrencyLoading && (
                  <p className="text-xs text-blue-600 mt-2">{t('common.loading')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Administrator Tab */}
        <TabsContent value="contact">
          <Card className="shadow-lg border-t-4 border-t-green-600">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>Contact Administrator</CardTitle>
                  <CardDescription>Get help or send feedback to the TurboBooks team</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">We're here to help!</p>
                    <p className="text-sm text-green-700">Our team typically responds within 24-48 hours.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="contact-subject">Subject</Label>
                  <Select value={contactSubject} onValueChange={setContactSubject}>
                    <SelectTrigger className="mt-2" data-testid="contact-subject-select">
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="billing">Billing & Subscription</SelectItem>
                      <SelectItem value="technical">Technical Support</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="bug">Report a Bug</SelectItem>
                      <SelectItem value="feedback">Feedback</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="contact-message">Message</Label>
                  <Textarea
                    id="contact-message"
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="Describe your question, issue, or feedback..."
                    rows={6}
                    className="mt-2"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  onClick={async () => {
                    if (!contactMessage.trim()) {
                      toast({
                        title: 'Message Required',
                        description: 'Please enter a message before sending.',
                        variant: 'destructive'
                      });
                      return;
                    }
                    
                    setSendingContact(true);
                    try {
                      // Send email via backend
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-admin`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${sessionManager.getToken()}`
                        },
                        body: JSON.stringify({
                          subject: contactSubject,
                          message: contactMessage,
                          user_email: currentUser?.email,
                          user_name: currentUser?.name
                        })
                      });
                      
                      if (response.ok) {
                        toast({
                          title: 'Message Sent!',
                          description: 'We\'ll get back to you soon.'
                        });
                        setContactMessage('');
                        setContactSubject('general');
                      } else {
                        throw new Error('Failed to send');
                      }
                    } catch (error) {
                      toast({
                        title: 'Error',
                        description: 'Failed to send message. Please try again or email turbofastmarket@gmail.com directly.',
                        variant: 'destructive'
                      });
                    } finally {
                      setSendingContact(false);
                    }
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 gap-2"
                  disabled={sendingContact}
                  data-testid="send-contact-btn"
                >
                  {sendingContact ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-500 text-center">
                  Or email us directly at{' '}
                  <a 
                    href="mailto:turbofastmarket@gmail.com" 
                    className="text-green-600 hover:underline font-medium"
                  >
                    turbofastmarket@gmail.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Tab (only for admin users) */}
        {isAdmin && (
          <TabsContent value="admin">
            {/* User Analytics Card */}
            <Card className="shadow-lg border-t-4 border-t-indigo-600 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-6 w-6 text-indigo-600" />
                    <div>
                      <CardTitle>User Analytics</CardTitle>
                      <CardDescription>Overview of app usage and users</CardDescription>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchUserStats} disabled={statsLoading} data-testid="refresh-stats-btn">
                    {statsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {statsLoading && !userStats ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                  </div>
                ) : userStats ? (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center" data-testid="stat-total-users">
                        <p className="text-3xl font-bold text-blue-700">{userStats.total_users}</p>
                        <p className="text-sm text-blue-600 mt-1">Total Users</p>
                      </div>
                      <button
                        type="button"
                        onClick={openSubscribers}
                        className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center cursor-pointer hover:from-green-100 hover:to-green-200 transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-400"
                        data-testid="stat-active-subs"
                        title="Click to see who is paying"
                      >
                        <p className="text-3xl font-bold text-green-700">{userStats.subscription?.active || 0}</p>
                        <p className="text-sm text-green-600 mt-1">Active Subscriptions</p>
                        <p className="text-[10px] text-green-700/70 mt-1 underline">View paying users →</p>
                      </button>
                      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center" data-testid="stat-trial-users">
                        <p className="text-3xl font-bold text-amber-700">{userStats.subscription?.trial || 0}</p>
                        <p className="text-sm text-amber-600 mt-1">Trial Users</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center" data-testid="stat-recent-signups">
                        <p className="text-3xl font-bold text-purple-700">{userStats.recent_signups_30d || 0}</p>
                        <p className="text-sm text-purple-600 mt-1">New (30 days)</p>
                      </div>
                    </div>

                    {/* Auth Provider Breakdown */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Sign-up Methods
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Google Login</span>
                            <Badge variant="outline" className="bg-red-50 text-red-700">{userStats.auth_providers?.google || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Email/Password</span>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">{userStats.auth_providers?.email || 0}</Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Platform Activity
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Businesses</span>
                            <Badge variant="outline">{userStats.content?.businesses || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Invoices</span>
                            <Badge variant="outline">{userStats.content?.invoices || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Estimates</span>
                            <Badge variant="outline">{userStats.content?.estimates || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Expenses</span>
                            <Badge variant="outline">{userStats.content?.expenses || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Clients</span>
                            <Badge variant="outline">{userStats.content?.clients || 0}</Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Products</span>
                            <Badge variant="outline">{userStats.content?.products || 0}</Badge>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* User Activity Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <h4 className="font-semibold text-gray-700">User Activity</h4>
                        <div className="flex items-center gap-2">
                          <Input
                            value={activitySearch}
                            onChange={(e) => setActivitySearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') refreshUserActivity(activitySearch); }}
                            placeholder="Search by name or email"
                            className="h-8 text-sm w-64"
                            data-testid="user-activity-search"
                          />
                          <Button size="sm" variant="outline" onClick={() => refreshUserActivity(activitySearch)} data-testid="user-activity-search-btn">
                            Search
                          </Button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="user-activity-table">
                          <thead className="bg-gray-50 text-gray-600">
                            <tr>
                              <th className="text-left px-3 py-2">User</th>
                              <th className="text-left px-3 py-2">Plan / Subscription</th>
                              <th className="text-right px-3 py-2">Last Active</th>
                              <th className="text-right px-3 py-2">Actions 7d / 30d / all</th>
                              <th className="text-right px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {(userActivity?.users || userStats.recent_users || []).map((u, i) => (
                              <tr key={u.user_id || i} className="hover:bg-gray-50" data-testid={`user-activity-row-${i}`}>
                                <td className="px-3 py-2 max-w-[260px]">
                                  <p className="font-medium truncate">{u.name || '-'}</p>
                                  <p className="text-xs text-gray-500 truncate" title={u.email}>{u.email}</p>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={
                                      u.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                                      u.subscription_status === 'trial' ? 'bg-amber-100 text-amber-700' :
                                      'bg-gray-100 text-gray-600'
                                    } data-testid={`user-plan-badge-${i}`}>
                                      {u.is_admin ? 'admin' : u.subscription_status === 'expired' ? 'free' : (u.subscription_status || 'free')}
                                    </Badge>
                                    {u.user_id && !u.is_admin && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className={`text-xs h-7 ${u.subscription_status === 'active' ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                                        onClick={() => { setSubEditUser(u); setSubEditMonths('indefinite'); }}
                                        data-testid={`manage-subscription-${u.user_id}`}
                                      >
                                        {u.subscription_status === 'active' ? 'Deactivate' : 'Activate'}
                                      </Button>
                                    )}
                                  </div>
                                  {u.subscription_status === 'active' && !u.is_admin && (
                                    <p className="text-[10px] text-gray-500 mt-1 whitespace-nowrap" data-testid={`user-plan-detail-${i}`}>
                                      {u.granted_by_admin ? 'Admin grant' : (u.payment_method || 'paid')}
                                      {formatSubEnd(u.subscription_end) ? ` · ${formatSubEnd(u.subscription_end)}` : ''}
                                    </p>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                                  {formatRelative(u.last_active_at)}
                                </td>
                                <td className="px-3 py-2 text-right font-mono whitespace-nowrap" title="7 days / 30 days / total">
                                  <span className="text-blue-700">{u.actions_7d ?? '-'}</span>
                                  <span className="text-gray-300"> / </span>
                                  <span className="text-indigo-700">{u.actions_30d ?? '-'}</span>
                                  <span className="text-gray-300"> / </span>
                                  <span className="text-gray-700">{u.total_actions ?? '-'}</span>
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {u.user_id && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs h-7"
                                      onClick={() => openUserActivityDetail(u)}
                                      data-testid={`view-activity-${u.user_id}`}
                                    >
                                      View
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {(userActivity?.users?.length === 0) && (
                              <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No matching users.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Visitor Analytics Card */}
            <Card className="shadow-lg border-t-4 border-t-emerald-600 mb-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Eye className="h-6 w-6 text-emerald-600" />
                  <div>
                    <CardTitle>Visitor Analytics</CardTitle>
                    <CardDescription>Website traffic and page views</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {statsLoading && !visitorStats ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  </div>
                ) : visitorStats ? (
                  <div className="space-y-6">
                    {/* Visitor Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center" data-testid="stat-total-visits">
                        <p className="text-3xl font-bold text-emerald-700">{visitorStats.total_visits?.toLocaleString()}</p>
                        <p className="text-sm text-emerald-600 mt-1">Total Page Views</p>
                      </div>
                      <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-4 text-center" data-testid="stat-unique-visitors">
                        <p className="text-3xl font-bold text-teal-700">{visitorStats.unique_visitors_30d?.toLocaleString()}</p>
                        <p className="text-sm text-teal-600 mt-1">Unique (30d)</p>
                      </div>
                      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-4 text-center" data-testid="stat-today-visits">
                        <p className="text-3xl font-bold text-cyan-700">{visitorStats.today?.toLocaleString()}</p>
                        <p className="text-sm text-cyan-600 mt-1">Today</p>
                      </div>
                      <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl p-4 text-center" data-testid="stat-week-visits">
                        <p className="text-3xl font-bold text-sky-700">{visitorStats.last_7_days?.toLocaleString()}</p>
                        <p className="text-sm text-sky-600 mt-1">Last 7 Days</p>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center" data-testid="stat-month-visits">
                        <p className="text-3xl font-bold text-blue-700">{visitorStats.last_30_days?.toLocaleString()}</p>
                        <p className="text-sm text-blue-600 mt-1">Last 30 Days</p>
                      </div>
                    </div>

                    {/* Daily Visits Chart */}
                    {visitorStats.daily_visits?.length > 0 && (
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-700 mb-4">Daily Page Views (Last 14 Days)</h4>
                        <div className="flex items-end gap-1 h-40">
                          {visitorStats.daily_visits.map((day, i) => {
                            const maxVisits = Math.max(...visitorStats.daily_visits.map(d => d.visits), 1);
                            const height = Math.max((day.visits / maxVisits) * 100, 2);
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-500 font-medium">{day.visits || ''}</span>
                                <div
                                  className="w-full bg-emerald-400 rounded-t transition-all hover:bg-emerald-500"
                                  style={{ height: `${height}%`, minHeight: '2px' }}
                                  title={`${day.date}: ${day.visits} visits`}
                                />
                                <span className="text-xs text-gray-400 -rotate-45 origin-top-left whitespace-nowrap">{day.date}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Top Pages */}
                      {visitorStats.top_pages?.length > 0 && (
                        <div className="border rounded-lg p-4">
                          <h4 className="font-semibold text-gray-700 mb-3">Top Pages (30d)</h4>
                          <div className="space-y-2">
                            {visitorStats.top_pages.map((page, i) => (
                              <div key={i} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600 truncate max-w-[200px]">{page.path || '/'}</span>
                                <Badge variant="outline">{page.count}</Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Device Breakdown */}
                      <div className="border rounded-lg p-4">
                        <h4 className="font-semibold text-gray-700 mb-3">Devices (30d)</h4>
                        <div className="space-y-3">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">Desktop</span>
                              <span className="font-medium">{visitorStats.devices?.desktop || 0}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${(visitorStats.devices?.desktop || 0) / Math.max((visitorStats.devices?.desktop || 0) + (visitorStats.devices?.mobile || 0), 1) * 100}%` }}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">Mobile</span>
                              <span className="font-medium">{visitorStats.devices?.mobile || 0}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className="bg-emerald-500 h-2 rounded-full transition-all"
                                style={{ width: `${(visitorStats.devices?.mobile || 0) / Math.max((visitorStats.devices?.desktop || 0) + (visitorStats.devices?.mobile || 0), 1) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No visitor data yet. Analytics will appear as users visit the site.</p>
                )}
              </CardContent>
            </Card>

            {/* Payment Settings Card */}
            <Card className="shadow-lg border-t-4 border-t-purple-600">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Crown className="h-6 w-6 text-purple-600" />
                  <div>
                    <CardTitle>Admin Settings</CardTitle>
                    <CardDescription>Configure payment receiving accounts</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* PayPal Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-yellow-600" />
                    PayPal Configuration
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="paypal_email">PayPal Email (for receiving payments)</Label>
                      <Input
                        id="paypal_email"
                        type="email"
                        value={paymentSettings.paypalEmail}
                        onChange={(e) => setPaymentSettings({...paymentSettings, paypalEmail: e.target.value})}
                        placeholder="your-paypal@email.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">This is where subscription payments will be sent</p>
                    </div>
                    <div>
                      <Label htmlFor="paypal_client_id">PayPal Client ID (optional)</Label>
                      <Input
                        id="paypal_client_id"
                        value={paymentSettings.paypalClientId}
                        onChange={(e) => setPaymentSettings({...paymentSettings, paypalClientId: e.target.value})}
                        placeholder="PayPal API Client ID"
                      />
                    </div>
                  </div>
                </div>

                {/* Stripe Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    Stripe Configuration
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="stripe_key">Stripe Public Key</Label>
                      <Input
                        id="stripe_key"
                        value={paymentSettings.stripePublicKey}
                        onChange={(e) => setPaymentSettings({...paymentSettings, stripePublicKey: e.target.value})}
                        placeholder="pk_live_..."
                      />
                      <p className="text-xs text-gray-500 mt-1">Get this from your Stripe Dashboard</p>
                    </div>
                  </div>
                </div>

                {/* Bank Transfer Settings */}
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-green-600" />
                    Bank Transfer (displayed on invoices)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="bank_name">Bank Name</Label>
                      <Input
                        id="bank_name"
                        value={paymentSettings.bankName}
                        onChange={(e) => setPaymentSettings({...paymentSettings, bankName: e.target.value})}
                        placeholder="Bank of America"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_account">Account Number</Label>
                      <Input
                        id="bank_account"
                        value={paymentSettings.bankAccount}
                        onChange={(e) => setPaymentSettings({...paymentSettings, bankAccount: e.target.value})}
                        placeholder="****1234"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bank_routing">Routing Number</Label>
                      <Input
                        id="bank_routing"
                        value={paymentSettings.bankRouting}
                        onChange={(e) => setPaymentSettings({...paymentSettings, bankRouting: e.target.value})}
                        placeholder="123456789"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button 
                    onClick={savePaymentSettings}
                    className="bg-purple-600 hover:bg-purple-700 gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save Payment Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Active Subscribers Dialog */}
      <Dialog open={subscribersDialogOpen} onOpenChange={setSubscribersDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="subscribers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-green-600" />
              Active Premium Subscribers
            </DialogTitle>
          </DialogHeader>
          {subscribersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : subscribers ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{subscribers.count}</p>
                  <p className="text-xs text-green-600">Paying Users</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">${subscribers.estimated_mrr?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-emerald-600">Estimated MRR</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-right px-3 py-2">Price</th>
                      <th className="text-left px-3 py-2">Renews</th>
                      <th className="text-right px-3 py-2">Last Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {subscribers.subscribers?.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No active subscribers yet.</td></tr>
                    ) : subscribers.subscribers?.map((s, i) => (
                      <tr key={s.user_id || i} className="hover:bg-gray-50" data-testid={`subscriber-row-${i}`}>
                        <td className="px-3 py-2 font-medium">{s.name || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{s.email}</td>
                        <td className="px-3 py-2">
                          <Badge className="bg-green-100 text-green-700 capitalize">{s.plan || 'monthly'}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">${(s.price || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                          {s.renews_at ? new Date(s.renews_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                          {formatRelative(s.last_active_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage User Subscription Dialog */}
      <Dialog open={!!subEditUser} onOpenChange={(open) => { if (!open) setSubEditUser(null); }}>
        <DialogContent className="max-w-md" data-testid="manage-subscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-blue-600" />
              Manage Subscription
            </DialogTitle>
            <CardDescription>{subEditUser?.name ? `${subEditUser.name} · ` : ''}{subEditUser?.email}</CardDescription>
          </DialogHeader>
          {subEditUser && (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-3 text-sm">
                <p className="text-gray-500">Current plan</p>
                <p className="font-semibold capitalize" data-testid="manage-subscription-current-status">
                  {subEditUser.subscription_status === 'expired' ? 'Free' : subEditUser.subscription_status}
                  {subEditUser.subscription_status === 'active' && (
                    <span className="font-normal text-gray-500"> · {subEditUser.granted_by_admin ? 'Admin grant' : (subEditUser.payment_method || 'paid')}{formatSubEnd(subEditUser.subscription_end) ? ` · ${formatSubEnd(subEditUser.subscription_end)}` : ''}</span>
                  )}
                </p>
              </div>

              {subEditUser.subscription_status === 'active' ? (
                <>
                  <p className="text-sm text-gray-600">Deactivating will immediately move this user to the <strong>Free</strong> plan (no trial).</p>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSubEditUser(null)} data-testid="manage-subscription-cancel-btn">Cancel</Button>
                    <Button className="bg-red-600 hover:bg-red-700" disabled={subEditLoading} onClick={() => submitSubscriptionChange('deactivate')} data-testid="manage-subscription-deactivate-btn">
                      {subEditLoading ? 'Working…' : 'Deactivate'}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Premium duration</Label>
                    <Select value={subEditMonths} onValueChange={setSubEditMonths}>
                      <SelectTrigger data-testid="manage-subscription-duration-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="indefinite">Indefinite (until deactivated)</SelectItem>
                        <SelectItem value="1">1 month</SelectItem>
                        <SelectItem value="3">3 months</SelectItem>
                        <SelectItem value="6">6 months</SelectItem>
                        <SelectItem value="12">12 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSubEditUser(null)} data-testid="manage-subscription-cancel-btn">Cancel</Button>
                    <Button className="bg-green-600 hover:bg-green-700" disabled={subEditLoading} onClick={() => submitSubscriptionChange('activate')} data-testid="manage-subscription-activate-btn">
                      {subEditLoading ? 'Working…' : 'Activate Premium'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* User Activity Detail Dialog */}
      <Dialog open={!!activityDetailUser} onOpenChange={(open) => { if (!open) { setActivityDetailUser(null); setActivityDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="user-activity-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-600" />
              {activityDetailUser?.name || activityDetailUser?.email}
            </DialogTitle>
            <CardDescription>{activityDetailUser?.email}</CardDescription>
          </DialogHeader>
          {!activityDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-gray-800">{activityDetail.user?.action_count || 0}</p>
                  <p className="text-xs text-gray-500">Total Actions</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-blue-700">{formatRelative(activityDetail.user?.last_active_at)}</p>
                  <p className="text-xs text-blue-500">Last Active</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-green-700 capitalize">{(activityDetail.user?.subscription || {}).status || 'free'}</p>
                  <p className="text-xs text-green-500">Plan</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b text-xs font-semibold text-gray-600">
                  Recent Actions ({activityDetail.activity?.length || 0})
                </div>
                <div className="max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody className="divide-y">
                      {activityDetail.activity?.length === 0 ? (
                        <tr><td className="px-3 py-4 text-center text-gray-400">No activity recorded yet.</td></tr>
                      ) : activityDetail.activity?.map((a, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 font-mono text-blue-600 whitespace-nowrap">{a.method}</td>
                          <td className="px-3 py-1.5 text-gray-700">{a.path}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500 whitespace-nowrap">
                            {a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Security Info */}
      <Card className="shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Shield className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-800">Your Data is Secure</h3>
              <p className="text-sm text-gray-600 mt-1">
                All payments are processed securely through Stripe, PayPal, and Lasco. We never store your credit card information.
                Your business data is encrypted and backed up regularly.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Settings;
